import { Injectable, NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Resolve,
  Router,
  RouterModule,
  Routes,
} from "@angular/router";
import { from, Observable } from "rxjs";
import { first, map, take } from "rxjs/operators";
import { AccountUserGroupService } from "../account-user-group/account-user-group.service";
import { AccountAdminGuard, LoggedInGuard } from "../auth/auth.guards";
import { PermissionService } from "../permission.service";
import { SessionService } from "../session.service";
import { ChangePasswordComponent } from "./change-password/change-password.component";
import { PreferencesComponent } from "./preferences/preferences.component";
import { UserGroupComponent } from "./user-groups/user-group/user-group.component";
import { UserGroupsListComponent } from "./user-groups/user-groups-list/user-groups-list.component";
import { UsersListComponent } from "./users-list/users-list.component";
import { UsersService } from "./users.service";

@Injectable({
  providedIn: "root",
})
class ListGuard implements CanActivate {
  constructor(
    private permissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.permissionService.categories.users.hasList()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
class WriteGuard implements CanActivate {
  constructor(
    private permissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.permissionService.categories.users.hasWrite()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

// @Injectable({ providedIn: "root" })
// export class UsersResolve implements Resolve<any> {
//   constructor(
//     private usersService: UsersService,
//     private sessionService: SessionService,
//   ) {}

//   resolve() {
//     const accountID = this.sessionService.currentAccountValue!.accountID;

//     return this.usersService.getUsers(accountID).pipe(take(1));
//   }
// }

@Injectable({ providedIn: "root" })
export class UserResolve implements Resolve<any> {
  constructor(
    private usersService: UsersService,
    private sessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.usersService
      .getUser(
        this.sessionService.currentAccountValue!.accountID,
        route.params.userID,
      )
      .pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class AccountUsersGroupResolve implements Resolve<any> {
  constructor(
    private accountUserGroupService: AccountUserGroupService,
    private sessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.accountUserGroupService
      .getAccountUserGroup(
        this.sessionService.currentAccountValue!.accountID,
        route.params.accountUserGroupID,
      )
      .pipe(first());
  }
}

@Injectable({ providedIn: "root" })
export class AccountUsersResolve implements Resolve<any> {
  constructor(
    private usersService: UsersService,
    private sessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.usersService
      .getAccountUsers(this.sessionService.currentAccountValue!.accountID)
      .pipe(first());
  }
}

@Injectable({ providedIn: "root" })
export class UserGroupsResolve implements Resolve<any> {
  constructor(
    private sessionService: SessionService,
    private accountUserGroupService: AccountUserGroupService,
  ) {}

  resolve() {
    return this.accountUserGroupService.getAccountUserGroups(
      this.sessionService.currentAccountValue!.accountID,
      {
        page: 0,
        perPage: 999999999,
      },
    );
  }
}

const routes: Routes = [
  {
    path: "",
    canActivate: [LoggedInGuard],
    runGuardsAndResolvers: "always",
    // resolve: { users: UsersResolve },
    data: { sidebar: "admin" },
    children: [
      {
        path: "list",
        component: UsersListComponent,
        canActivate: [AccountAdminGuard, ListGuard],
        data: { title: "Users List" },
      },
      {
        path: "changePassword",
        component: ChangePasswordComponent,
        data: { title: "Change Password" },
        pathMatch: "full",
      },
      {
        path: "groups",
        canActivate: [AccountAdminGuard],
        children: [
          {
            path: "list",
            data: { title: "User Groups" },
            component: UserGroupsListComponent,
            resolve: { userGroups: UserGroupsResolve },
            canActivate: [AccountAdminGuard],
          },
          {
            path: "create",
            data: { title: "Create User Group" },
            component: UserGroupComponent,
            resolve: { accountUsers: AccountUsersResolve },
          },
          {
            path: ":accountUserGroupID",
            data: { title: "Edit User Group" },
            component: UserGroupComponent,
            resolve: {
              userGroup: AccountUsersGroupResolve,
              accountUsers: AccountUsersResolve,
            },
          },
        ],
      },
      {
        path: "create",
        data: { title: "Create User" },
        canActivate: [WriteGuard],

        pathMatch: "full",
        component: PreferencesComponent,
      },
      {
        path: ":userID",
        resolve: { user: UserResolve },
        runGuardsAndResolvers: "always",
        children: [
          {
            path: "preferences",
            component: PreferencesComponent,
            canActivate: [WriteGuard],

            resolve: {},
            data: { title: "User Preferences" },
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class UsersRoutingModule {}

const usersModuleLazyLoadRoutes: Routes = [
  {
    path: "users",
    loadChildren: () => import("./users.module").then((m) => m.UserModule),
  },
];

export { usersModuleLazyLoadRoutes };
